import {
    takeLatest,
    put,
    call,
    race,
    takeMaybe,
    select,
    all,
    fork,
    delay,
} from 'redux-saga/effects';
import getBrowserHistory from 'yoda-core-components/lib/navigation/history/getBrowserHistory';
import find from 'lodash/find';
import map from 'lodash/map';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import reduce from 'lodash/reduce';
import uniq from 'lodash/uniq';
import flatten from 'lodash/flatten';
import groupBy from 'lodash/groupBy';
import isArray from 'lodash/isArray';
import includes from 'lodash/includes';
import time from 'yoda-core-components/lib/moment/Time';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import makeQuery from 'yoda-core-components/lib/navigation/query/makeQuery';
import { serviceErrorToAnalyticsFormat } from 'yoda-site-components/lib/analytics/DigitalAnalyticsHelper';
import AnalyticsAction from 'yoda-site-components/lib/actions/AnalyticsAction';
import {
    selectCurbsideGetPollingResponse,
    selectGetCurbsidePickupOrderInfo,
} from 'yoda-account-components/lib/selectors/curbsideSelector';
import TokenProvider, {
    setValue,
} from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';
import {
    postCurbsideCheckInInfo,
    getCurbsideCheckInInfo,
    deleteCancelCurbside,
    getStoreForCurbsideValidation,
    getCurbsidePickupOrderInfo,
} from 'yoda-interfaces/lib/Order/OrderApi';
import FactorySaga from 'yoda-site-components/lib/sagas/FactorySaga';
import {
    selectMessagesTexts,
    selectPreferences,
} from 'yoda-site-components/lib/selectors/ContextSelector';
import {
    stopPollingGetCurbsideCheckInInfo,
    curbsidePickupAnalyticsAction,
    resetCurbsideCheckInInfo,
} from '../actions/CurbsideActions';
import {
    POST_CURBSIDE_CHECKIN_INFO,
    POST_CURBSIDE_CHECKIN_INFO_SUCCESS,
    POST_CURBSIDE_CHECKIN_INFO_ERROR,
    GET_CURBSIDE_CHECKIN_INFO_SUCCESS,
    GET_CURBSIDE_CHECKIN_INFO_ERROR,
    POLL_GET_CURBSIDE_CHECKIN_INFO,
    STOP_POLLING_GET_CURBSIDE_CHECKIN_INFO,
    GET_CURBSIDE_PICKUP_ORDER_INFO_SUCCESS,
    GET_CURBSIDE_PICKUP_ORDER_INFO_ERROR,
    GET_CURBSIDE_PICKUP_ORDER_INFO,
    GET_CURBSIDE_PICKUP_STORE_INFO,
    GET_CURBSIDE_PICKUP_STORE_INFO_SUCCESS,
    GET_CURBSIDE_PICKUP_STORE_INFO_ERROR,
    PATCH_CANCEL_CURBSIDE,
    PATCH_CANCEL_CURBSIDE_SUCCESS,
    PATCH_CANCEL_CURBSIDE_ERROR,
    GET_CURBSIDE_CHECKIN_INFO_SERVICE_DOWN,
    GET_CURBSIDE_CHECKIN_INFO_SERVICE_UP,
    GET_INSIDE_STORE,
} from '../actionTypes/CurbsideActionTypes';
import { storage } from '../common/Constants';

// TODO: Use this for CAMUI-2463
function getCurbsideCheckinInfoFromStorage() {
    return TokenProvider.get(storage.DP_CURBSIDE_CHECKIN_INFO) || {};
}

function* storeCurbsideCheckinInfo(checkinInfo = {}) {
    // clears the previous value and resets
    setValue(storage.DP_CURBSIDE_CHECKIN_INFO, checkinInfo);
    yield;
}

function* storeCurbsideProductImages(productImages = []) {
    // clears the previous value and resets
    setValue(storage.DP_CURBSIDE_PRODUCT_INFO, productImages);
    yield;
}

function* redirectToStatusPage(fromEmail) {
    const searchParams = fromEmail ? `fromEmail=${fromEmail}` : '';
    const preferences = yield select(selectPreferences);
    getBrowserHistory().push({
        pathname: get(
            preferences,
            'navigateToCurbsideStatusPage.mobile',
            '/orders/curbside/status'
        ),
        search: searchParams,
    });
}

function* redirectToLandingPage(orderNumber) {
    const { location } = getBrowserHistory();
    const query = makeQuery(location.search);
    const fromEmail = get(query, 'fromEmail', '');
    if (fromEmail) {
        getBrowserHistory().push({ pathname: '/' });
    } else {
        /** TODO - for Guest User redirect to trackorderPage
         *  Now - This is edgecase
         *  LoggedIn - withsomeCurbside - will render available curbside orders
         *  LoggedIn - withoutCurbside - will render unable to lookup order page
         */
        const searchParams = orderNumber ? `?orderNumber=${orderNumber}` : '';
        const preferences = yield select(selectPreferences);
        getBrowserHistory().push({
            pathname: get(preferences, 'navigateToCurbsideLandingPage.mobile', '/orders/curbside'),
            search: searchParams,
        });
    }
}

function* clearStorageAndRedirect(orderId) {
    LocalStorage.removeData(storage.DP_CURBSIDE_CHECKIN_INFO);
    LocalStorage.removeData(storage.DP_CURBSIDE_PRODUCT_INFO);
    yield fork(redirectToLandingPage, orderId);
    yield put(resetCurbsideCheckInInfo());
    yield put(stopPollingGetCurbsideCheckInInfo());
}

function* cancelCurbsideSuccess(statusScreenValues, canceledResponse) {
    const statusValues = {
        cancelStatusTitle: get(statusScreenValues, 'confirmationModal.successTitle', ''),
        cancelStatusSubtitle: get(statusScreenValues, 'confirmationModal.successInfo', ''),
    };
    LocalStorage.removeData(storage.DP_CURBSIDE_CHECKIN_INFO);
    LocalStorage.removeData(storage.DP_CURBSIDE_PRODUCT_INFO);
    yield all([
        put(stopPollingGetCurbsideCheckInInfo()),
        put({
            type: PATCH_CANCEL_CURBSIDE_SUCCESS,
            canceledResponse,
            statusValues,
        }),
    ]);
}

function* processOpenRequest(fromEmail, payload, canRedirectToStatusPage) {
    /* If this call is fired to check open requests,
    after success check for product info in local storage.
    If present, redirect to status page.
    If not, fire get orders and stores call
    */
    const isOrderInfoPresentInStorage = TokenProvider.get(storage.DP_CURBSIDE_PRODUCT_INFO) || [];
    if (!isEmpty(isOrderInfoPresentInStorage) && canRedirectToStatusPage) {
        yield fork(redirectToStatusPage, fromEmail);
    } else {
        yield put({
            type: GET_CURBSIDE_PICKUP_ORDER_INFO,
            payload,
            config: { isOpenRequest: true, fromEmail, canRedirectToStatusPage },
        });
    }
}

function* postCurbsideCheckInInfoSaga(action) {
    try {
        const response = yield call(FactorySaga, postCurbsideCheckInInfo, action, undefined, false);
        if (response.isSuccess) {
            const { fromEmail, selectedOrderProducts } = action.config;
            const preferences = yield select(selectPreferences);
            const curbsideConfig = get(preferences, 'curbside', {});
            // expiresIn Setting from config
            const expiresIn = get(curbsideConfig, 'expires', [60, 'm']) || [];
            const data = {
                ...get(response, 'response.data'),
                expires: time()
                    .add(...expiresIn)
                    .format(), // Reference: https://momentjs.com/docs/#/manipulating/add/
            };
            yield all([
                put({ type: POST_CURBSIDE_CHECKIN_INFO_SUCCESS, response: data }),
                fork(storeCurbsideCheckinInfo, data),
                fork(storeCurbsideProductImages, selectedOrderProducts),
            ]);
            yield fork(redirectToStatusPage, fromEmail);
        } else {
            const error = get(response, 'response.data', {
                errorCode: 'EXCEPTION_IN_POST_CURBSIDE_CHECKIN_SAGA',
            });
            // Mapping Error to pass it to DA
            const errorPayload = {
                errorCode: error.errorCode ? error.errorCode : get(error, 'code', ''),
                errorMessage: error.errorMessage ? error.errorMessage : get(error, 'message', ''),
            };
            const errorDetails = serviceErrorToAnalyticsFormat(errorPayload);

            yield all([
                put(AnalyticsAction.triggerFormError(errorDetails)),
                put({
                    type: POST_CURBSIDE_CHECKIN_INFO_ERROR,
                    error: response,
                }),
            ]);
        }
    } catch (error) {
        const errorPayload = {
            errorMessage: error.message,
            errorCode: 'EXCEPTION_IN_POST_CURBSIDE_CHECKIN_SAGA',
        };
        const errorDetails = serviceErrorToAnalyticsFormat(errorPayload);
        yield all([
            put(AnalyticsAction.triggerFormError(errorDetails)),
            put({
                type: POST_CURBSIDE_CHECKIN_INFO_ERROR,
                error: errorPayload,
            }),
        ]);
    }
}

function* getCurbsideCheckInInfoSaga(action) {
    const {
        payload: { retryOnFailure, stopAfterRetries, curbsideParams },
        config: { isOpenRequest, fromEmail } = {},
    } = action;
    // Use refreshIntervalFromConfig incase 1st API call fails
    const preferences = yield select(selectPreferences);
    const refreshIntervalFromConfig = get(preferences, 'curbside.refreshInterval', 120) * 1000;
    let refreshInterval = refreshIntervalFromConfig;
    const stats = {
        retries: 0, // retry when failed scenerios
    };
    while (true) {
        try {
            const response = yield call(
                FactorySaga,
                getCurbsideCheckInInfo,
                action,
                undefined,
                false
            );
            if (response.isSuccess) {
                const responseData = get(response, 'response.data', {});
                const requestStatus = get(responseData, 'status.requestStatus', '');
                const status = ['CANCELLED', 'COMPLETED', 'STOPPED', 'CLOSED'];
                yield put({ type: GET_CURBSIDE_CHECKIN_INFO_SERVICE_UP });
                const canRedirectToStatusPage =
                    responseData.active &&
                    requestStatus !== 'CLOSED' &&
                    requestStatus !== 'CANCELLED';
                if (!responseData.active && includes(status, requestStatus)) {
                    /** if polling becomes inactive/cancelled
                     * in other devices/ cancelled from stores
                     */
                    const messagesTexts = yield select(selectMessagesTexts);
                    const statusScreenValues = get(messagesTexts, 'curbside.statusScreen', {});
                    yield fork(cancelCurbsideSuccess, statusScreenValues, response);
                    /* eslint-disable no-unused-expressions */
                    isOpenRequest
                        ? yield fork(
                              processOpenRequest,
                              fromEmail,
                              action.payload,
                              canRedirectToStatusPage
                          )
                        : '';
                    // This needs to be called to get the info from getCheckInInfoDetails for DA & COMPLETED CANCELLED POPUP
                    /* eslint-disable no-unused-expressions */
                    canRedirectToStatusPage
                        ? yield put({
                              type: GET_CURBSIDE_CHECKIN_INFO_SUCCESS,
                              response: responseData,
                          })
                        : '';
                } else {
                    refreshInterval = responseData.refreshInterval * 1000;
                    const dataToBackup = {
                        ...responseData,
                        orderIdClicked: curbsideParams.orderId,
                    };
                    yield all([
                        put({ type: GET_CURBSIDE_CHECKIN_INFO_SUCCESS, response: responseData }),
                        call(storeCurbsideCheckinInfo, dataToBackup),
                    ]);
                    /* eslint-disable no-unused-expressions */
                    isOpenRequest
                        ? yield fork(
                              processOpenRequest,
                              fromEmail,
                              action.payload,
                              canRedirectToStatusPage
                          )
                        : '';
                    stats.retries = 0;
                }
            } else {
                const reduxPollingResponse = yield select(selectCurbsideGetPollingResponse);
                const backUpData = reduxPollingResponse.data || getCurbsideCheckinInfoFromStorage();
                const error = get(response, 'response.data', {
                    errorCode: 'GET_CURBSIDE_CHECKIN_INFO_ERROR',
                });
                const {
                    location: { pathname },
                } = getBrowserHistory();
                const errorStatus = get(response, 'response.status');
                const curbsideStatusURL = get(
                    preferences,
                    'navigateToCurbsideStatusPage.mobile',
                    '/orders/curbside/status'
                );
                if (errorStatus === 404 && pathname === curbsideStatusURL) {
                    const checkedInOrderDetails = get(backUpData, 'orders', []);
                    const orderId = get(checkedInOrderDetails[0], 'orderId', '');
                    yield fork(clearStorageAndRedirect, orderId);
                }
                const checkErrorStatus = errorStatus === 422 && pathname === curbsideStatusURL;
                const isRetryFailed = get(action, 'payload.failedRetry', false);
                if (checkErrorStatus && !isRetryFailed) {
                    yield put({ type: GET_CURBSIDE_CHECKIN_INFO_SERVICE_DOWN });
                } else if (checkErrorStatus && isRetryFailed) {
                    yield put({ type: GET_INSIDE_STORE });
                } else {
                    yield put({ type: GET_CURBSIDE_CHECKIN_INFO_SERVICE_UP });
                }
                if (!isEmpty(backUpData)) {
                    yield put({ type: GET_CURBSIDE_CHECKIN_INFO_SUCCESS, response: backUpData });
                } else {
                    const errorDetails = serviceErrorToAnalyticsFormat(error);
                    yield all([
                        put(AnalyticsAction.triggerFormError(errorDetails)),
                        put({ type: GET_CURBSIDE_CHECKIN_INFO_ERROR, error: response }),
                    ]);
                    /* In case of error while checking open request,
                    fire get orders and stores call to show on
                    store selection page */
                    const getPickUpOrderInfo = yield select(selectGetCurbsidePickupOrderInfo);
                    const orderResponseData = get(getPickUpOrderInfo, 'data', {});
                    if (isOpenRequest && isEmpty(orderResponseData)) {
                        yield put({
                            type: GET_CURBSIDE_PICKUP_ORDER_INFO,
                            payload: action.payload,
                        });
                    }
                }
                stats.retries += 1;
            }
        } catch (err) {
            stats.retries += 1;
            const errorPayload = {
                errorMessage: err.message,
                errorCode: 'EXCEPTION_IN_GET_CURBSIDE_CHECKIN_SAGA',
            };
            const errorDetails = serviceErrorToAnalyticsFormat(errorPayload);

            yield all([
                put(AnalyticsAction.triggerFormError(errorDetails)),
                put({ type: GET_CURBSIDE_CHECKIN_INFO_ERROR, error: err }),
            ]);
        }
        /* checks if it's not open request
        and if retry is needed and max attempts not reached.
        if yes then delay and retry
        if not stop polling */
        const shouldRetry = retryOnFailure && stats.retries < stopAfterRetries && !isOpenRequest;
        shouldRetry ? yield delay(refreshInterval) : yield put(stopPollingGetCurbsideCheckInInfo());
    }
}

function* pollGetCurbsideCheckInInfo(action) {
    // polling logic
    yield race([
        call(getCurbsideCheckInInfoSaga, action),
        takeMaybe(STOP_POLLING_GET_CURBSIDE_CHECKIN_INFO),
    ]);
}

function* patchCancelCurbside(action) {
    try {
        const messagesTexts = yield select(selectMessagesTexts);
        const statusScreenValues = get(messagesTexts, 'curbside.statusScreen', {});
        // Moving from HTTP PATCH to HTTP DELETE,
        // TODO: Need to rename the function and related constants once this is finalised
        const canceledResponse = yield call(
            FactorySaga,
            deleteCancelCurbside,
            action,
            undefined,
            false
        );
        if (get(canceledResponse, 'response.status', '') === 200) {
            yield fork(cancelCurbsideSuccess, statusScreenValues, canceledResponse);
        } else {
            const errorValue = get(canceledResponse, 'response.data', { errorCode: '' });
            const isSuccess = get(canceledResponse, 'isSuccess', true);
            let statusValues = {};
            if (
                get(errorValue, 'errorCode', '') ===
                    get(statusScreenValues, 'errorCodes.cancelFailedErrorCode', '') ||
                !isSuccess
            ) {
                statusValues = {
                    cancelStatusTitle: get(
                        statusScreenValues,
                        'confirmationModal.failureTitle',
                        ''
                    ),
                    cancelStatusSubtitle: get(
                        statusScreenValues,
                        'confirmationModal.failureInfo',
                        ''
                    ),
                };
            } else {
                statusValues = {
                    cancelStatusTitle: get(statusScreenValues, 'confirmationModal.errorTitle', ''),
                    cancelStatusSubtitle: get(
                        statusScreenValues,
                        'confirmationModal.errorInfo',
                        ''
                    ),
                };
            }
            const errorDetails = serviceErrorToAnalyticsFormat(errorValue);
            yield all([
                put(AnalyticsAction.triggerFormError(errorDetails)),
                put({
                    type: PATCH_CANCEL_CURBSIDE_ERROR,
                    canceledError: errorValue,
                    statusValues,
                }),
            ]);
        }
    } catch (error) {
        const errorPayload = {
            errorMessage: error.message,
            errorCode: 'EXCEPTION_IN_PATCH_CANCEL_CURBSIDE_SAGA',
        };
        const errorDetails = serviceErrorToAnalyticsFormat(errorPayload);
        yield all([
            put(AnalyticsAction.triggerFormError(errorDetails)),
            put({
                type: PATCH_CANCEL_CURBSIDE_ERROR,
                canceledError: errorPayload,
                statusValues: {},
            }),
        ]);
    }
}

/**
    params :ArrayTocheck - Array, Keys - key to be returned, orderId -default undefined
    return : Array, based on key or particular Obj
    Eg:['4044', 2785] [{},{}]
 */
/* eslint-disable no-param-reassign */
function recursiveStoreIdFetch(ArrTocheck, keys, InitialValue = [], orderId, contact) {
    return reduce(
        ArrTocheck,
        (results, eachValue) => {
            const isLinesAvailable = get(eachValue, 'lines');
            if (isLinesAvailable && isArray(isLinesAvailable)) {
                orderId = eachValue.id;
                contact = eachValue.contact;
                recursiveStoreIdFetch(eachValue.lines, keys, results, orderId, contact);
            } else if (!isEmpty(get(eachValue, 'store'))) {
                eachValue.orderId = !keys && orderId;
                eachValue.contact = !keys && contact;
                keys ? results.push(eachValue[keys]) : results.push(eachValue);
            }
            return results;
        },
        InitialValue
    );
}

/**
    params :orderResponse - Array, storeResponse - Array
    return : Array, by grouping based on stores and clubbing with productInfo
 */
function groupByStore(orderResponse, storeResponse) {
    const result = recursiveStoreIdFetch(orderResponse);
    const groupedObj = groupBy(result, 'store');
    return storeResponse.map((eachValue) => {
        const clonedValue = { ...eachValue };
        const values = eachValue.id;
        if (groupedObj[values]) {
            clonedValue.orders = groupedObj[values];
        }
        return clonedValue;
    });
}

function getAllProductDetails(groupedStoreData) {
    const getOrders = map(groupedStoreData, (result) => get(result, 'lines', []));
    const flatTheOrders = flatten(getOrders) || [];
    const parseProductInformation = flatTheOrders.map((el) => ({
        productInfo: {
            productLotId: [get(el, 'product.lotNumber', '')],
            productPPID: get(el, 'product.ppId', ''),
            productSKU: get(el, 'product.id', ''),
            curbsideOrderId: get(el, 'orderId', ''),
        },
    }));
    return parseProductInformation;
}

function parseStoreAndProductInfo(groupedStoreData, getProductDetails) {
    const filterStoreDetails = {
        storeDetails: map(
            groupedStoreData,
            (storeDetails) =>
                `${get(storeDetails, 'id', 'noStoreId')}^${
                    get(storeDetails, 'curbside.coordinates.url', '') ? 'yes' : 'no'
                }^${!!get(storeDetails, 'phone', false)}`
        ).join('|'),
        productDetails: getProductDetails,
    };
    return filterStoreDetails;
}

function* getCurbsidePickupStoreInfo(actionParam) {
    try {
        const action = { ...actionParam };
        const {
            config: { isOpenRequest, fromEmail, canRedirectToStatusPage } = {},
            payload: { orderResponseData } = {},
        } = action;
        // if its MultiOrder then API response will Array or else singleOrder its :object
        const orderDataBasedOnOrder = isArray(orderResponseData)
            ? orderResponseData
            : [orderResponseData];
        action.payload.storeId = uniq(recursiveStoreIdFetch(orderDataBasedOnOrder, 'store')).join(
            ','
        ); // fetches unique storeId
        const response = yield call(
            FactorySaga,
            getStoreForCurbsideValidation,
            action,
            undefined,
            false
        );
        const storeResponseData = get(response, 'response.data');
        const isStoreResponseBlocked = get(response, 'response.error.message', '');
        if (response.isSuccess) {
            const groupedStoreData = groupByStore(orderDataBasedOnOrder, storeResponseData);
            if (isOpenRequest && canRedirectToStatusPage) {
                /* If action came from open request,
                filter selected order info and store it in localstorage.
                Later, redirect to status page. */
                const checkinInfo = TokenProvider.get(storage.DP_CURBSIDE_CHECKIN_INFO) || {};
                const selectedStore =
                    find(
                        groupedStoreData,
                        (storeData) => storeData.id === get(checkinInfo, 'store.id', '')
                    ) || {};
                const selectedOrderProducts = map(selectedStore.orders, (line) => line.product);
                yield call(storeCurbsideProductImages, selectedOrderProducts);
                yield fork(redirectToStatusPage, fromEmail);
            }
            const getProductDetails = getAllProductDetails(orderDataBasedOnOrder);
            const storeAndProductInfo = parseStoreAndProductInfo(
                groupedStoreData,
                getProductDetails
            );
            yield all([
                put(curbsidePickupAnalyticsAction(storeAndProductInfo)),
                put({ type: GET_CURBSIDE_PICKUP_STORE_INFO_SUCCESS, response: groupedStoreData }),
            ]);
        } else {
            const error = get(response, 'response.data', {
                errorCode: 'GET_CURBSIDE_PICKUP_STORE_INFO_ERROR',
            });
            const errorDetails = serviceErrorToAnalyticsFormat(error);

            yield all([
                put(AnalyticsAction.triggerFormError(errorDetails)),
                put({
                    type: GET_CURBSIDE_PICKUP_STORE_INFO_ERROR,
                    error: storeResponseData || isStoreResponseBlocked,
                }),
            ]);
        }
    } catch (error) {
        const errorPayload = {
            errorMessage: error.message,
            errorCode: 'EXCEPTION_IN_GET_CURBSIDE_PICKUP_STORE_SAGA',
        };
        const errorDetails = serviceErrorToAnalyticsFormat(errorPayload);
        yield all([
            put(AnalyticsAction.triggerFormError(errorDetails)),
            put({
                type: GET_CURBSIDE_PICKUP_STORE_INFO_ERROR,
                error: errorPayload,
            }),
        ]);
    }
}
function* getCurbsidePickupOrderInfoSaga(actionParam) {
    try {
        const action = { ...actionParam };
        const { config } = action;
        const userState = TokenProvider.get('DP_USER_STATE');
        action.payload.isLoggedIn = userState === '1';
        const response = yield call(
            FactorySaga,
            getCurbsidePickupOrderInfo,
            action,
            undefined,
            false
        );
        const orderResponseData = get(response, 'response.data');
        const isOrderResponseBlocked = get(response, 'response.error.message', '');
        if (response.isSuccess) {
            yield all([
                put({
                    type: GET_CURBSIDE_PICKUP_STORE_INFO,
                    payload: {
                        orderResponseData,
                    },
                    config,
                }), // Pass config containing isOpenRequest to handle redirection to status page
                put({ type: GET_CURBSIDE_PICKUP_ORDER_INFO_SUCCESS, response: orderResponseData }),
            ]);
        } else {
            const error = get(response, 'response.data', {
                errorCode: 'GET_CURBSIDE_PICKUP_ORDER_INFO_ERROR',
            });
            const errorDetails = serviceErrorToAnalyticsFormat(error);

            yield all([
                put(AnalyticsAction.triggerFormError(errorDetails)),
                put({
                    type: GET_CURBSIDE_PICKUP_ORDER_INFO_ERROR,
                    error: orderResponseData || isOrderResponseBlocked,
                }),
            ]);
        }
    } catch (error) {
        const errorPayload = {
            errorMessage: error.message,
            errorCode: 'EXCEPTION_IN_GET_CURBSIDE_PICKUP_ORDER_SAGA',
        };
        const errorDetails = serviceErrorToAnalyticsFormat(errorPayload);
        yield all([
            put(AnalyticsAction.triggerFormError(errorDetails)),
            put({
                type: GET_CURBSIDE_PICKUP_ORDER_INFO_ERROR,
                error: errorPayload,
            }),
        ]);
    }
}
function* watchCurbsideSaga() {
    yield takeLatest(POST_CURBSIDE_CHECKIN_INFO, postCurbsideCheckInInfoSaga);
    yield takeLatest(POLL_GET_CURBSIDE_CHECKIN_INFO, pollGetCurbsideCheckInInfo);
    yield takeLatest(PATCH_CANCEL_CURBSIDE, patchCancelCurbside);
    yield takeLatest(GET_CURBSIDE_PICKUP_ORDER_INFO, getCurbsidePickupOrderInfoSaga);
    yield takeLatest(GET_CURBSIDE_PICKUP_STORE_INFO, getCurbsidePickupStoreInfo);
}

watchCurbsideSaga.sagaName = 'curbsideSaga';
export default watchCurbsideSaga;
